import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPaymentInfo, formatAmount } from '../services/guestPaymentService';
import './GuestPaymentSuccess.css';

const GuestPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');

  const token = searchParams.get('token');
  const success = searchParams.get('success') === 'true';

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!token) {
        setError('Invalid payment link');
        setLoading(false);
        return;
      }

      try {
        // Small delay to allow webhook to process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const data = await getPaymentInfo(token);
        setPaymentInfo(data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch payment info:', err);
        setError('Unable to load payment information');
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="guest-payment-success-container">
        <div className="loading-card">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h2>Verifying Payment</h2>
          <p>Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guest-payment-success-container">
        <div className="status-card error-card">
          <div className="status-icon error-icon">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h2>Something Went Wrong</h2>
          <p>{error}</p>
          <div className="action-buttons">
            <button className="btn-primary-action" onClick={() => navigate('/')}>
              <i className="bi bi-house me-2"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment was not successful (user cancelled or card declined)
  if (!success) {
    return (
      <div className="guest-payment-success-container">
        <div className="status-card failed-card">
          <div className="status-icon failed-icon">
            <i className="bi bi-x-circle-fill"></i>
          </div>
          <h2>Payment Not Completed</h2>
          <p>Your payment was not completed. No charges were made.</p>

          <div className="reservation-info">
            <h4>{paymentInfo?.restaurantName}</h4>
            <p>
              {formatDate(paymentInfo?.reservationDate)} at{' '}
              {formatTime(paymentInfo?.reservationTime)}
            </p>
          </div>

          <div className="failed-info">
            <p>This could be due to:</p>
            <ul>
              <li>Payment was cancelled</li>
              <li>Card was declined</li>
              <li>Insufficient funds</li>
              <li>Network issues</li>
            </ul>
          </div>

          <div className="action-buttons">
            <button
              className="btn-primary-action"
              onClick={() => navigate(`/pay/${token}`)}
            >
              <i className="bi bi-arrow-repeat me-2"></i>
              Try Again
            </button>
            <button className="btn-secondary-action" onClick={() => navigate('/')}>
              <i className="bi bi-house me-2"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if fully paid
  if (paymentInfo?.isFullyPaid) {
    return (
      <div className="guest-payment-success-container">
        <div className="status-card success-card">
          <div className="status-icon success-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <h2>Payment Complete!</h2>
          <p>All guests have been paid for. Your reservation is confirmed!</p>

          <div className="booking-details">
            <h3>Reservation Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <i className="bi bi-building"></i>
                <div>
                  <span className="detail-label">Restaurant</span>
                  <span className="detail-value">{paymentInfo.restaurantName}</span>
                </div>
              </div>
              <div className="detail-item">
                <i className="bi bi-calendar3"></i>
                <div>
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    {formatDate(paymentInfo.reservationDate)}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <i className="bi bi-clock"></i>
                <div>
                  <span className="detail-label">Time</span>
                  <span className="detail-value">
                    {formatTime(paymentInfo.reservationTime)}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <i className="bi bi-people"></i>
                <div>
                  <span className="detail-label">Guests</span>
                  <span className="detail-value">{paymentInfo.totalGuests} Guests</span>
                </div>
              </div>
              <div className="detail-item">
                <i className="bi bi-credit-card"></i>
                <div>
                  <span className="detail-label">Total Paid</span>
                  <span className="detail-value">
                    EGP {formatAmount(paymentInfo.totalPaidCents)}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <i className="bi bi-person"></i>
                <div>
                  <span className="detail-label">Booked By</span>
                  <span className="detail-value">{paymentInfo.guestName}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="confirmation-message">
            <i className="bi bi-envelope-check me-2"></i>
            A confirmation email has been sent to the reservation holder.
          </div>

          <div className="action-buttons">
            <button className="btn-primary-action" onClick={() => navigate('/')}>
              <i className="bi bi-house me-2"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Partial payment (more guests to pay)
  return (
    <div className="guest-payment-success-container">
      <div className="status-card partial-card">
        <div className="status-icon partial-icon">
          <i className="bi bi-check-circle-fill"></i>
        </div>
        <h2>Payment Successful!</h2>
        <p>Your payment has been received.</p>

        <div className="booking-details">
          <h3>Reservation Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <i className="bi bi-building"></i>
              <div>
                <span className="detail-label">Restaurant</span>
                <span className="detail-value">{paymentInfo?.restaurantName}</span>
              </div>
            </div>
            <div className="detail-item">
              <i className="bi bi-calendar3"></i>
              <div>
                <span className="detail-label">Date</span>
                <span className="detail-value">
                  {formatDate(paymentInfo?.reservationDate)}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <i className="bi bi-clock"></i>
              <div>
                <span className="detail-label">Time</span>
                <span className="detail-value">
                  {formatTime(paymentInfo?.reservationTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="payment-progress-section">
          <h4>Payment Progress</h4>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${(paymentInfo?.paidGuestCount / paymentInfo?.totalGuests) * 100}%`,
              }}
            />
          </div>
          <div className="progress-stats">
            <span>
              <strong>{paymentInfo?.paidGuestCount}</strong> of{' '}
              <strong>{paymentInfo?.totalGuests}</strong> guests paid
            </span>
            <span>
              EGP {formatAmount(paymentInfo?.totalPaidCents || 0)} / EGP{' '}
              {formatAmount(paymentInfo?.totalRequiredCents || 0)}
            </span>
          </div>
        </div>

        <div className="remaining-info">
          <i className="bi bi-info-circle me-2"></i>
          <strong>{paymentInfo?.remainingGuests}</strong> guest
          {paymentInfo?.remainingGuests !== 1 ? 's' : ''} still need to pay (EGP{' '}
          {formatAmount(paymentInfo?.remainingAmountCents || 0)} remaining)
        </div>

        <div className="share-section">
          <p>Share this link with your group to collect remaining payments:</p>
          <div className="share-link-box">
            <input
              type="text"
              readOnly
              value={`${window.location.origin}/pay/${token}`}
              className="share-link-input"
            />
            <button
              className="btn-copy"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/pay/${token}`);
                alert('Link copied to clipboard!');
              }}
            >
              <i className="bi bi-clipboard"></i>
            </button>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="btn-primary-action"
            onClick={() => navigate(`/pay/${token}`)}
          >
            <i className="bi bi-credit-card me-2"></i>
            Pay for More Guests
          </button>
          <button className="btn-secondary-action" onClick={() => navigate('/')}>
            <i className="bi bi-house me-2"></i>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestPaymentSuccess;
